/**
 * El único archivo de la capa de medición que toca el DOM.
 *
 * Todo lo de al lado es puro y se prueba sin navegador; acá se saca el
 * ImageData y se aplica el catálogo. Mantener la frontera ahorra tener que
 * levantar un navegador para probar un umbral.
 */
import { Cara, ModeloDeDocumento, ZonaDeModelo, cajaEnPixeles, zonasDe } from "./catalogo";
import { EsquinasDeTarjeta, cajaEnLaImagen } from "./redaccion";
import { Rectangulo } from "./types";
import { evaluarZona } from "./legibilidad";
import { ImagenPlana, medirZona } from "./quality";
import { LegibilidadDeZona } from "./types";
import { ANCHO_OCR } from "./tuning";

/**
 * Achica el canvas al ancho de trabajo si hace falta, y devuelve sus píxeles.
 *
 * NUNCA amplía, y eso importa: ampliar no agrega detalle, lo diluye, y la foto
 * queda midiendo más borrosa de lo que es. Medido sobre el corpus: llevando
 * todo a 1600 px, las fotos de 1021 px nativos salían con la mitad de nitidez
 * de la que tienen en realidad, solo por haberlas estirado.
 */
export function pixelesNormalizados(
  c: HTMLCanvasElement,
  anchoObjetivo = ANCHO_OCR
): ImagenPlana | null {
  if (c.width === 0 || c.height === 0) return null;
  const factor = Math.min(1, anchoObjetivo / c.width);
  const w = Math.max(1, Math.round(c.width * factor));
  const h = Math.max(1, Math.round(c.height * factor));

  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  const ctx = out.getContext("2d");
  if (!ctx) return null;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(c, 0, 0, w, h);
  const d = ctx.getImageData(0, 0, w, h);
  return { data: d.data, width: w, height: h };
}

export interface OpcionesDeCara {
  /** Ancho de la tarjeta enderezada, en píxeles. null si no se pudo ubicar:
   *  entonces no hay escala y el alto de renglón no se puede afirmar. */
  anchoTarjetaPx?: number | null;
  /**
   * Las esquinas de la tarjeta en el canvas. Con ellas las zonas se miden en su
   * lugar y a la escala de la tarjeta (zonasAMedir), y no como fracciones del
   * canvas, que es un recorte con margen.
   */
  tarjeta?: EsquinasDeTarjeta | null;
}

/**
 * Las zonas de texto de esta cara, llevadas a la tarjeta ubicada (en píxeles de
 * una imagen reducida por `escala`), y el ancho real de la tarjeta en el canvas.
 *
 * Recorrido 2: la escala salía del ancho del recorte, que lleva un 6 % de margen
 * por lado, y las zonas eran fracciones de ese recorte. Las zonas que se tapan
 * (el Nº de Trámite) no se miden: nadie las va a leer.
 */
export function zonasAMedir(
  modelo: ModeloDeDocumento,
  cara: Cara,
  tarjeta: EsquinasDeTarjeta,
  escala: number
): { zonas: Array<{ campo: string; caja: Rectangulo; zona: ZonaDeModelo }>; anchoTarjetaPx: number } {
  const [si, sd, id, ii] = tarjeta;
  const anchoTarjetaPx = (Math.hypot(sd.x - si.x, sd.y - si.y) + Math.hypot(id.x - ii.x, id.y - ii.y)) / 2;
  const enLaImagen = tarjeta.map((p) => ({ x: p.x * escala, y: p.y * escala })) as EsquinasDeTarjeta;
  const zonas = zonasDe(modelo, cara)
    .filter((z) => (z.tipo === "texto" || z.tipo === "mrz") && !z.sensible)
    .map((z) => ({ campo: z.campo, caja: cajaEnLaImagen(z.caja, enLaImagen), zona: z }))
    .filter((z): z is { campo: string; caja: Rectangulo; zona: ZonaDeModelo } => !!z.caja);
  return { zonas, anchoTarjetaPx };
}

/**
 * Mide cada zona que el modelo declara para esta cara.
 *
 * Sin modelo reconocido se mide la cara entera como una sola zona: es menos
 * útil —no puede decir "el domicilio está tapado por el reflejo", solo "hay
 * reflejo"— pero sigue detectando el desenfoque y el brillo, que es lo que más
 * rechaza fotos en la práctica.
 */
export function medirCara(
  canvas: HTMLCanvasElement,
  modelo: ModeloDeDocumento | null,
  cara: Cara,
  opciones: OpcionesDeCara = {}
): LegibilidadDeZona[] {
  const img = pixelesNormalizados(canvas);
  if (!img) return [];
  return medirCaraEnPixeles(img, modelo, cara, opciones, img.width / canvas.width);
}

/** Alto nominal del renglón cuando no hay modelo: el de los datos de un DNI, 2,2 mm. */
const RENGLON_SIN_MODELO_MM = 2.2;

/**
 * La medición, sin DOM: `img` son los píxeles del canvas reducidos por `escala`.
 *
 * Sin modelo se mide la cara entera; con la tarjeta ubicada además se aplica el
 * piso del renglón con el alto nominal de 2,2 mm (revisión del recorrido 2: los
 * dorsos casi nunca tienen modelo, y sin escala el renglón chico no avisaba).
 */
export function medirCaraEnPixeles(
  img: ImagenPlana,
  modelo: ModeloDeDocumento | null,
  cara: Cara,
  opciones: OpcionesDeCara,
  escala: number
): LegibilidadDeZona[] {
  if (!modelo) {
    if (!opciones.tarjeta) return [evaluarZona(cara, medirZona(img))];
    const [si, sd, id, ii] = opciones.tarjeta;
    const anchoTarjetaPx = (Math.hypot(sd.x - si.x, sd.y - si.y) + Math.hypot(id.x - ii.x, id.y - ii.y)) / 2;
    return [evaluarZona(cara, medirZona(img, undefined, { anchoTarjetaPx, altoRenglonMm: RENGLON_SIN_MODELO_MM }))];
  }

  if (opciones.tarjeta) {
    const { zonas, anchoTarjetaPx } = zonasAMedir(modelo, cara, opciones.tarjeta, escala);
    if (zonas.length === 0) return [evaluarZona(cara, medirZona(img))];
    return zonas.map(({ campo, caja, zona }) =>
      evaluarZona(
        campo,
        medirZona(img, caja, { anchoTarjetaPx, altoRenglonMm: zona.altoRenglonMm })
      )
    );
  }

  const medibles = zonasDe(modelo, cara).filter(
    (z) => (z.tipo === "texto" || z.tipo === "mrz") && !z.sensible
  );
  if (medibles.length === 0) return [evaluarZona(cara, medirZona(img))];

  return medibles.map((z) =>
    evaluarZona(
      z.campo,
      medirZona(img, cajaEnPixeles(z.caja, img.width, img.height), {
        anchoTarjetaPx: opciones.anchoTarjetaPx ?? null,
        altoRenglonMm: z.altoRenglonMm,
      })
    )
  );
}
